import { Button, Icon } from '@ps/isp-web-component-library';
import { Padlock } from '@ps/isp-web-component-library/icons/Padlock';
import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { useTrackers } from '@ps/web-shared-analytics';
import classNames from 'clsx';
import React from 'react';
import { useField } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import { getIsAvailableOpportunity } from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import type { Dispatch } from '@swf/core/store';
import { useHasPromotionAdded } from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useHasPromotionAdded';

import { calculateTotalPotentialReturns } from '../../../../../../utils/calculateTotalPotentialReturns';
import PromotionControl from '../PromotionControl/PromotionControl';

import { TXTWCL_Max_30 } from './Stake.messages.json';
import styles from './Stake.scss';
import StakeInput from './components/StakeInput/StakeInput';

interface Props {
  className?: string;
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const Stake: React.FC<Props> = ({ className, opportunity }) => {
  const dispatch = useDispatch<Dispatch>();
  const isMaxStakeCtaEnabled = useFeature(FeatureFlag.ISPEnableMaxStakeCTA);
  const { buttonPressed } = useTrackers();
  const { formatMessage } = useRegulatoryIntl();
  const { input: stakeInput } = useField<string | undefined, HTMLInputElement>(
    opportunity.id + '.stakePerLine'
  );
  const { input: opportunityStateInput } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });
  const { hasPromotionAdded } = useHasPromotionAdded(opportunity.id);

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);

  const winPotentialReturn = isAvailableOpportunity
    ? calculateTotalPotentialReturns({
        stakePerLine: opportunityStateInput.value?.stakePerLineParsed || 0,
        placeReturnFactor: opportunity.placeReturnFactor,
        returnFactor: opportunity.returnFactor,
        numberOfLines: opportunity.numberOfLines,
        eachWaySelected: opportunityStateInput.value?.eachWaySelected,
        isFreeBet: Boolean(opportunityStateInput.value?.freeBet?.length)
      })
    : 0;

  const actualNumberOfLines = isAvailableOpportunity
    ? opportunityStateInput.value?.eachWaySelected
      ? opportunity.numberOfLines * 2
      : opportunity.numberOfLines
    : 0;

  const isWinPotentialReturnAboveMaxPayout =
    isAvailableOpportunity && opportunity.maxPayout
      ? winPotentialReturn / 100 > opportunity.maxPayout
      : false;

  const numberOfLines = isAvailableOpportunity
    ? actualNumberOfLines || opportunity.numberOfLines
    : 0;

  const maxStakeAmount = isAvailableOpportunity
    ? opportunityStateInput.value?.eachWaySelected
      ? opportunity.eachWayMax
      : opportunity.maxStake
    : 0;

  const maxStakeTranslation = formatMessage(TXTWCL_Max_30);

  const handleMaxStakeCtaPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (hasPromotionAdded) {
      return;
    }

    const newInputValue = String(maxStakeAmount);

    buttonPressed({
      buttonName: maxStakeTranslation
    });

    stakeInput.onChange(newInputValue);

    dispatch.betSlip.updateStakeCursorPosition({
      selectionStart: newInputValue.length,
      selectionEnd: newInputValue.length
    });
  };

  return (
    <div className={className}>
      <div
        className={classNames(styles.inputWrapper, {
          [styles.inputDisabled]: !isAvailableOpportunity
        })}
      >
        {!isAvailableOpportunity && <Icon icon={Padlock} className={styles.suspended} size="x1" />}

        <StakeInput
          fieldName={opportunity.id}
          minStake={isAvailableOpportunity ? opportunity.minStake : 0}
          maxStake={maxStakeAmount}
          minLineStake={isAvailableOpportunity ? opportunity.minLineStake : 0}
          minStakeIncrement={isAvailableOpportunity ? opportunity.minStakeIncrement : 0}
          maxPayout={isAvailableOpportunity ? opportunity.maxPayout : 0}
          isWinPotentialReturnAboveMaxPayout={isWinPotentialReturnAboveMaxPayout}
          numberOfLines={numberOfLines}
          opportunity={opportunity}
        />

        <PromotionControl
          fieldName={opportunity.id}
          numberOfLines={numberOfLines}
          opportunity={opportunity}
        />
      </div>

      {isMaxStakeCtaEnabled && (
        <Button
          className={styles.maxStakeCta}
          variant="tertiary"
          size="small"
          onPointerDown={handleMaxStakeCtaPointerDown}
          isWithSpaceAround={false}
          disabled={!isAvailableOpportunity || hasPromotionAdded}
        >
          ({maxStakeTranslation})
        </Button>
      )}
    </div>
  );
};

export default Stake;
